import { Box, Button, Chip, makeStyles, Typography } from '@material-ui/core';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useGetProtectedObject } from 'common/hooks/storage/useGetProtectedObject';
import { useNotifier } from 'common/hooks/useNotifier';
import Message from 'common/models/Message';
import { Doctor } from 'common/types/doctor';
import profilePlaceholder from 'img/profile-placeholder.png';
import vlogo from 'img/VSURGIC-VONLYLOGO-orange.png';
import moment from 'moment';
import { Link } from 'react-router-dom';

// @ts-ignore
const styles = theme => ({
    facilityBoxSeparator: {
        borderRight: `1px solid ${theme.palette.primary.main}`,
    },
    relatedFacilities: {
        background: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    doctorContentCard: {
        display: 'flex',
        height: '100%',
        // marginBottom: theme.spacing(1),
    },
    filterButton: {
        padding: '0 0 0 5px',
        fontSize: '0.8rem',
        border: '1px solid #ddd',
        background: '#eee',
        borderRadius: 0,
        margin: 0,
        display: 'flex',
        justifyContent: 'space-between',
    },
    filterButtonCaret: {
        fontSize: '20px',
    },
    filterMenu: {
        fontSize: '0.8rem',
        padding: '5px 15px',
    },
    filterResult: {
        padding: '5px 5px',
        color: '#2b333f',
        fontFamily: 'sans-serif',
        fontWeight: 600,
        fontSize: '0.75rem',
    },
    boxDoctorAvatar: {
        width: '200px',
        height: '200px',
        background: '#bbb',
        borderRadius: '50%',
        border: '1px solid #bbb',
        display: 'block',
    },
    boxDoctorAvaterOverlay: {
        position: 'absolute',
        top: '150px',
        right: '0',
        width: '57px',
        height: '52px',
        zIndex: '1',
        // filter: 'drop-shadow(1px 1px 5px rgba(0, 0, 0, 0.5))',
    },
    boxDoctorName: {
        color: '#333',
        fontWeight: '700',
        fontSize: '1.1rem',
        flex: '1',
    },
    boxVLogo: {
        width: '57px',
        height: 'auto',
    },
    boxVLogoSmaller: {
        width: '40px',
        height: 'auto',
    },
    boxDoctorScpecialty: {
        marginTop: '-20px',
    },
    boxDoctorChipRoot: {
        display: 'flex',
        flexWrap: 'wrap',
        '& > *': {
            margin: theme.spacing(0.8),
        },
        '& .MuiChip-root': {
            background: '#cee2f3',
            padding: '7px 7px',
            color: '#618db3',
            fontSize: '12px',
        },
        marginTop: '10px',
    },
    boxDoctorBio: {
        // margin: '10px 0',
        maxHeight: '120px',
        overflow: 'hidden',
        // textOverflow: 'ellipsis',
    },
    boxDoctorFullBio: {
        // margin: '10px 0',
    },
    websiteLink: {
        color: '#ff5722',
        textDecoration: 'none',
        cursor: 'pointer',
        fontSize: '12px',
    },
    dpMenu: {
        '& ul': {
            width: '120px',
        },
    },
    txtCounter: {
        fontSize: '15px',
    },
    textSecondary: {
        color: '#ff5722',
        fontSize: '15px',
    },
});

// @ts-ignore
const useStyles = makeStyles(styles);

interface DoctorCardProps {
    doctor: Doctor;
    goToFacilityDetails?: (facilityId: number) => void;
    showFullBio?: boolean;
}

export const DoctorCard = ({ doctor, goToFacilityDetails, showFullBio }: DoctorCardProps) => {
    const classes = useStyles();
    const doctorPhoto = useGetProtectedObject(doctor.photoUrl);
    const { notify } = useNotifier();

    const doctorAvatar = doctorPhoto ?? profilePlaceholder;
    const showingRatings = doctor.doctorRatings && doctor.doctorRatings.length > 0;
    const averageRating = showingRatings ? doctor.doctorRatings.reduce((acc, rating) => acc + rating.rating, 0) / doctor.doctorRatings.length : 0;

    const copyToClipboard = async () => {
        try {
            const url = window.location.origin + `/doctors/${doctor.id}`;
            await navigator.clipboard.writeText(url);
            notify(new Message({ title: 'Link Copied!', type: 'success' }));
        } catch (err) {
            notify(new Message({ title: 'Failed to copy link', type: 'error' }));
        }
    };

    return (
        <Box>
            {/* everything but bio */}
            <Box
                sx={{
                    display: 'flex',
                    padding: '10px',
                    flexWrap: 'wrap',
                }}
                className={classes.doctorContentCard}
            >
                {/* image */}
                <Box sx={{ flex: '0 auto content', position: 'relative', marginRight: '15px' }}>
                    <Link to={`/doctors/${doctor.id}`}>
                        <img src={doctorAvatar} alt={doctor.name} className={classes.boxDoctorAvatar} />
                        {showingRatings && <img src={vlogo} alt={'VSURGIC V Logo'} className={classes.boxDoctorAvaterOverlay} />}
                    </Link>
                </Box>
                {/* everything but image & bio */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {/* name/spec/website */}
                        <Box sx={{ flex: 1 }}>
                            <div>
                                <Link
                                    to={`/doctors/${doctor.id}`}
                                    style={{ color: '#333', fontWeight: '700', fontSize: '1.1rem', whiteSpace: 'nowrap' }}
                                    className={classes.boxDoctorName}
                                >
                                    {doctor.name}
                                </Link>
                            </div>
                            <div>
                                <Typography variant="caption">{doctor.specialty}</Typography>
                            </div>
                            {doctor.websiteUrl && (
                                <div>
                                    <a href={doctor.websiteUrl} target="_blank" rel="noopener noreferrer external" className={classes.websiteLink}>
                                        {doctor.websiteUrl}
                                    </a>
                                </div>
                            )}
                        </Box>
                        {/* average rating */}
                        {showingRatings && (
                            <Box
                                sx={{ flex: '1 0.001 content' }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <img src={vlogo} alt={'VSURGIC V Logo'} className={classes.boxVLogoSmaller} />
                                <span style={{ color: '#FF9011', fontSize: '40px', fontFamily: 'Libre Baskerville, serif' }}>
                                    {averageRating.toFixed(0)}
                                </span>
                            </Box>
                        )}
                        {/* totals */}
                        <Box
                            sx={{
                                padding: '10px',
                                border: '1px solid #dadfe1',
                                borderRadius: '15px',
                                display: 'flex',
                                flex: '0 0 content',
                                maxWidth: '100%',
                                alignSelf: 'center',
                            }}
                            style={{ gap: '10px' }}
                        >
                            <Box sx={{ flex: 'auto' }}>
                                <strong className={classes.txtCounter}>All: </strong>
                                <strong className={classes.textSecondary}>{doctor?.procedures?.length ?? 0}</strong>
                            </Box>
                            <Box sx={{ flex: 'auto' }}>
                                <strong className={classes.txtCounter}>This Week: </strong>
                                <strong className={classes.textSecondary}>
                                    {doctor?.procedures?.filter(x => moment(x.createdAt) >= moment().add(-1, 'w')).length ?? 0}
                                </strong>
                            </Box>
                            <Box sx={{ flex: 'auto' }}>
                                <strong className={classes.txtCounter}>This Month: </strong>
                                <strong className={classes.textSecondary}>
                                    {doctor?.procedures?.filter(x => moment(x.createdAt) >= moment().add(-1, 'M')).length ?? 0}
                                </strong>
                            </Box>
                            <Box sx={{ flex: 'auto' }}>
                                <strong className={classes.txtCounter}>This Year: </strong>
                                <strong className={classes.textSecondary}>
                                    {doctor?.procedures?.filter(x => moment(x.createdAt) >= moment().add(-1, 'y')).length ?? 0}
                                </strong>
                            </Box>
                        </Box>
                    </Box>
                    {/* ratings/types */}
                    <Box>
                        {!showingRatings && (
                            <Box className={classes.boxDoctorChipRoot} style={{ paddingBottom: '10px' }}>
                                {doctor.surgeryTypes.map((type: string) => (
                                    <Chip key={type} size="small" label={type} />
                                ))}
                            </Box>
                        )}
                        {showingRatings && (
                            <Box sx={{ display: 'flex' }} className={classes.boxDoctorChipRoot}>
                                {doctor.doctorRatings.map((rating, index) => (
                                    <Box key={index}>
                                        <Chip
                                            size="small"
                                            label={rating.category
                                                .split(' ')
                                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(' ')}
                                        />
                                        <Typography
                                            align="center"
                                            component="div"
                                            style={{ width: '100%', color: '#FF9011', fontSize: '40px', fontFamily: 'Libre Baskerville, serif' }}
                                        >
                                            {rating.rating}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    {/* share */}
                    <Box>
                        <Button size="small" variant="outlined" endIcon={<IosShareIcon />} onClick={copyToClipboard}>
                            Share
                        </Button>
                    </Box>
                </Box>
            </Box>
            {/* bio */}
            <Box sx={{ padding: '10px' }}>
                <Typography variant="caption" paragraph className={!showFullBio ? classes.boxDoctorBio : classes.boxDoctorFullBio}>
                    <strong>Biography:</strong> {doctor.bio}
                </Typography>
            </Box>
        </Box>
    );
};
